/**
 * Application monitoring and error tracking setup
 * Configures Sentry for production error monitoring
 */

import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';

/**
 * Initialize Sentry for error monitoring and performance tracking
 * Only initializes in production environment
 */
export function initializeMonitoring() {
  // Only initialize Sentry in production
  if (process.env.NODE_ENV === 'production' && process.env.REACT_APP_SENTRY_DSN) {
    Sentry.init({
      dsn: process.env.REACT_APP_SENTRY_DSN,
      integrations: [
        // Browser tracing removed due to version compatibility
      ],
      
      // Performance Monitoring
      tracesSampleRate: 0.1, // 10% of transactions for performance monitoring
      
      // Environment and release tracking
      environment: process.env.NODE_ENV,
      release: process.env.REACT_APP_VERSION || '1.0.0',
      
      // Error filtering
      beforeSend(event) {
        // Filter out non-critical errors in production
        if (event.exception) {
          const error = event.exception.values?.[0];
          
          // Skip network errors that users can't control
          if (error?.type === 'ChunkLoadError' || 
              error?.type === 'NetworkError' ||
              error?.value?.includes('Loading chunk') ||
              error?.value?.includes('Network request failed')) {
            return null;
          }
          
          // Skip common browser extension errors
          if (error?.value?.includes('extension://') ||
              error?.value?.includes('chrome-extension://') ||
              error?.value?.includes('moz-extension://')) {
            return null;
          }
        }
        
        return event;
      },
      
      // Set user context
      initialScope: {
        tags: {
          component: 'games-app',
        },
      },
    });

    // Set up performance monitoring for Core Web Vitals
    if (typeof window !== 'undefined') {
      import('web-vitals').then(({ onCLS, onFCP, onLCP, onTTFB }) => {
        onCLS((metric: any) => {
          Sentry.addBreadcrumb({
            category: 'web-vitals',
            message: 'CLS',
            data: { value: metric.value, rating: getVitalsRating(metric.value, [0.1, 0.25]) },
            level: 'info',
          });
        });
        
        
        onFCP((metric: any) => {
          Sentry.addBreadcrumb({
            category: 'web-vitals',
            message: 'FCP',
            data: { value: metric.value, rating: getVitalsRating(metric.value, [1800, 3000]) },
            level: 'info',
          });
        });
        
        onLCP((metric: any) => {
          Sentry.addBreadcrumb({
            category: 'web-vitals',
            message: 'LCP',
            data: { value: metric.value, rating: getVitalsRating(metric.value, [2500, 4000]) },
            level: 'info',
          });
        });
        
        onTTFB((metric: any) => {
          Sentry.addBreadcrumb({
            category: 'web-vitals',
            message: 'TTFB',
            data: { value: metric.value, rating: getVitalsRating(metric.value, [800, 1800]) },
            level: 'info',
          });
        });
      });
    }
    
    console.log('Sentry monitoring initialized');
  } else if (process.env.NODE_ENV === 'development') {
    console.log('Sentry monitoring disabled in development');
  }
}

/**
 * Rate Web Vitals metrics
 */
function getVitalsRating(value: number, thresholds: [number, number]): 'good' | 'needs-improvement' | 'poor' {
  if (value <= thresholds[0]) return 'good';
  if (value <= thresholds[1]) return 'needs-improvement';
  return 'poor';
}

/**
 * Manually capture exceptions with context
 */
export function captureError(error: Error, context?: Record<string, any>) {
  if (process.env.NODE_ENV === 'production') {
    Sentry.withScope((scope) => {
      if (context) {
        scope.setContext('additional_context', context);
      }
      Sentry.captureException(error);
    });
  } else {
    console.error('Captured error:', error, context);
  }
}

/**
 * Add user context to error reports
 */
export function setUserContext(user: { id: string; email?: string; subscription?: string }) {
  if (process.env.NODE_ENV === 'production') {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      subscription_tier: user.subscription,
    });
  }
}

/**
 * Clear user context (on logout)
 */
export function clearUserContext() {
  if (process.env.NODE_ENV === 'production') {
    Sentry.setUser(null);
  }
}

/**
 * Add custom breadcrumbs for debugging
 */
export function addBreadcrumb(message: string, category: string, data?: Record<string, any>) {
  if (process.env.NODE_ENV === 'production') {
    Sentry.addBreadcrumb({
      message,
      category,
      data,
      level: 'info',
      timestamp: Date.now() / 1000,
    });
  }
}

/**
 * Performance monitoring for custom operations
 */
export function startTransaction(name: string, op: string) {
  if (process.env.NODE_ENV === 'production') {
    // Transaction API updated in newer Sentry versions
    return Sentry.startSpan({ name, op }, () => {});
  }
  return null;
}

/**
 * Sentry Error Boundary HOC for React components
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary;